import * as React from "react";
import PropTypes from "prop-types";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    IconButton,
    Tooltip,
    TablePagination,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Typography,
    CircularProgress,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Divider,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "../items/UseToken";

export default function GenericDataTable({
    pageUrl,
    fetchUrl,
    ovverideColumns,
    rowActions,
    refreshTrigger,
    tableName = "Data",
    emptyState,
    readyState,
    deleteEndpoint, // ✅ opcjonalny: jak jest => pokazujemy delete + modal
}) {
    const [rows, setRows] = useState([]);
    const [headCells, setHeadCells] = useState([]);
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("start_date");
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dense, setDense] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [filters, setFilters] = useState({});
    const hiddenData = ["password", "robotMagicKey"];

    // loading state (ładniejszy UX w topbar)
    const [loading, setLoading] = useState(false);

    // delete dialog state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    const navigate = useNavigate();
    const { token } = useToken();

    const canDelete = Boolean(deleteEndpoint);

    const fetchData = () => {
        navigate(pageUrl);

        setLoading(true);

        fetch(fetchUrl, {
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
            },
        })
            .then((res) => res.json())
            .then((res) => {
                const results = res.results || [];
                setRows(results);

                const keys =
                    ovverideColumns?.length
                        ? ovverideColumns
                        : results.length > 0
                            ? Object.keys(results[0])
                            : [];

                const headArray = keys.map((key) => ({
                    id: key,
                    label: key.replace(/_/g, " ").toUpperCase(),
                }));

                setHeadCells(headArray);
            })
            .catch((err) => console.error("Error fetching data:", err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchUrl, refreshTrigger]);

    const handleFilterChange = (id, value) => {
        setFilters({ ...filters, [id]: value });
        setPage(0);
    };

    const clearFilters = () => {
        setFilters({});
        setPage(0);
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const filteredRows = useMemo(() => {
        return rows.filter((row) =>
            Object.keys(filters).every((key) =>
                row[key]
                    ?.toString()
                    .toLowerCase()
                    .includes((filters[key] ?? "").toLowerCase())
            )
        );
    }, [rows, filters]);

    const showEmpty = filteredRows.length === 0;

    // ✅ selection helpers
    const rowId = (row) => row.guid ?? row.id; // prefer guid, fallback to id
    const isRowSelected = (row) => selected.indexOf(rowId(row)) !== -1;

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const allIds = filteredRows.map((r) => rowId(r)).filter(Boolean);
            setSelected(allIds);
            return;
        }
        setSelected([]);
    };

    const handleRowCheckboxClick = (event, row) => {
        const id = rowId(row);
        if (!id) return;

        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
        else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
        else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
        else if (selectedIndex > 0) newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));

        setSelected(newSelected);
    };

    // ✅ delete flow
    const openDeleteConfirm = () => {
        setDeleteError("");
        setConfirmOpen(true);
    };

    const closeDeleteConfirm = () => {
        if (deleting) return;
        setConfirmOpen(false);
    };

    const confirmDelete = async () => {
        if (!canDelete || selected.length === 0) {
            setConfirmOpen(false);
            return;
        }

        setDeleting(true);
        setDeleteError("");

        try {
            await Promise.all(
                selected.map((guid) =>
                    fetch(deleteEndpoint, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: "Bearer " + token,
                        },
                        body: JSON.stringify({ guid }),
                    }).then(async (r) => {
                        if (!r.ok) {
                            const txt = await r.text().catch(() => "");
                            throw new Error(txt || `Delete failed for ${guid}`);
                        }
                    })
                )
            );

            setSelected([]);
            setConfirmOpen(false);
            fetchData();
        } catch (e) {
            setDeleteError(e?.message || "Failed to delete one or more records.");
        } finally {
            setDeleting(false);
        }
    };

    const hasAnyFilter = useMemo(() => {
        return Object.values(filters).some((v) => (v ?? "").toString().length > 0);
    }, [filters]);

    // headerLoaded zostawiam tak jak miałeś: to gate'uje “loading view”
    const headerLoaded = headCells.length > 0;

    return (
        <>
            {!headerLoaded ? (
                <Box sx={{ p: 10, alignItems: "center" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={30} thickness={5} />
                        <Typography variant="header" color="text.secondary">
                            Loading {tableName}
                        </Typography>
                    </Stack>
                </Box>
            ) : showEmpty ? (
                <Box sx={{ p: 4 }}>
                    {emptyState ?? (
                        <Typography variant="body1" color="text.secondary">
                            {tableName ? `No ${tableName} found.` : "No results found."}
                        </Typography>
                    )}
                </Box>
            ) : (
                <>
                    {readyState}

                    <Box sx={{ width: "100%" }}>
                        <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{
                                width: "100%",
                                mb: 2,
                                borderRadius: 3,
                                overflow: "hidden",
                            }}
                        >
                            {/* Top bar */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Typography sx={{ fontWeight: 900 }} variant="subtitle1">
                                    {tableName}
                                </Typography>

                                {loading && (
                                    <Chip
                                        size="small"
                                        label="Refreshing…"
                                        icon={<CircularProgress size={14} thickness={5} />}
                                        sx={{ ml: 0.5 }}
                                    />
                                )}

                                <Box sx={{ flex: 1 }} />

                                {selected.length > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${selected.length} selected`}
                                        sx={{ mr: 0.5 }}
                                    />
                                )}

                                <Tooltip title="Refresh">
                                    <span>
                                        <IconButton
                                            onClick={fetchData}
                                            disabled={loading}
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 2,
                                            }}
                                            size="small"
                                        >
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Tooltip title={showFilter ? "Hide filters" : "Show filters"}>
                                    <IconButton
                                        onClick={() => setShowFilter((prev) => !prev)}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: showFilter ? "primary.main" : "divider",
                                            borderRadius: 2,
                                        }}
                                        size="small"
                                    >
                                        <FilterListIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {canDelete && selected.length > 0 && (
                                    <Tooltip title={`Delete (${selected.length})`}>
                                        <IconButton
                                            onClick={openDeleteConfirm}
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "error.main",
                                                borderRadius: 2,
                                                color: "error.main",
                                            }}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            <Divider />

                            {/* Filter Row */}
                            {showFilter && (
                                <Box sx={{ p: 2, bgcolor: "background.paper" }}>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                            Filters
                                        </Typography>

                                        <Box sx={{ flex: 1, width: "100%" }}>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            {headCells.map((cell) => (
                                                                <TableCell key={cell.id} sx={{ py: 1 }}>
                                                                    {cell.id === "status" ? (
                                                                        <FormControl variant="standard" sx={{ minWidth: 120 }}>
                                                                            <InputLabel>Status</InputLabel>
                                                                            <Select
                                                                                value={filters[cell.id] || ""}
                                                                                onChange={(e) => handleFilterChange(cell.id, e.target.value)}
                                                                            >
                                                                                <MenuItem value="">All</MenuItem>
                                                                                <MenuItem value="Successful">Successful</MenuItem>
                                                                                <MenuItem value="Failed">Failed</MenuItem>
                                                                                <MenuItem value="In progress">In progress</MenuItem>
                                                                            </Select>
                                                                        </FormControl>
                                                                    ) : (
                                                                        <TextField
                                                                            variant="standard"
                                                                            label={cell.label}
                                                                            value={filters[cell.id] ?? ""}
                                                                            onChange={(e) => handleFilterChange(cell.id, e.target.value)}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                            ))}
                                                            <TableCell align="right" sx={{ whiteSpace: "nowrap", py: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    onClick={clearFilters}
                                                                    disabled={!hasAnyFilter}
                                                                    sx={{ textTransform: "none", fontWeight: 700 }}
                                                                >
                                                                    Clear
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}

                            {/* Table */}
                            <TableContainer
                                sx={{
                                    maxHeight: dense ? 520 : 640,
                                }}
                            >
                                <Table stickyHeader size={dense ? "small" : "medium"}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox" sx={{ bgcolor: "background.paper" }}>
                                                <Checkbox
                                                    checked={filteredRows.length > 0 && selected.length === filteredRows.length}
                                                    indeterminate={selected.length > 0 && selected.length < filteredRows.length}
                                                    onChange={handleSelectAllClick}
                                                />
                                            </TableCell>

                                            {headCells.map((headCell) => (
                                                <TableCell
                                                    key={headCell.id}
                                                    sx={{
                                                        bgcolor: "background.paper",
                                                        fontWeight: 900,
                                                        letterSpacing: 0.3,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    <TableSortLabel
                                                        active={orderBy === headCell.id}
                                                        direction={orderBy === headCell.id ? order : "asc"}
                                                        onClick={(e) => handleRequestSort(e, headCell.id)}
                                                    >
                                                        {headCell.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}

                                            {rowActions && (
                                                <TableCell
                                                    sx={{
                                                        bgcolor: "background.paper",
                                                        fontWeight: 900,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    Actions
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {filteredRows
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row) => (
                                                <TableRow
                                                    key={rowId(row) ?? JSON.stringify(row)}
                                                    hover
                                                    sx={{
                                                        "&:hover td": { bgcolor: "action.hover" },
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={isRowSelected(row)}
                                                            onClick={(e) => handleRowCheckboxClick(e, row)}
                                                        />
                                                    </TableCell>

                                                    {headCells.map((cell) => (
                                                        <TableCell key={cell.id} sx={{ verticalAlign: "top" }}>
                                                            {hiddenData.includes(cell.id)
                                                                ? "**********"
                                                                : cell.id === "picture" && row[cell.id] ? (
                                                                    <img
                                                                        src={row[cell.id]}
                                                                        alt="Thumbnail"
                                                                        style={{
                                                                            width: 44,
                                                                            height: 44,
                                                                            objectFit: "cover",
                                                                            borderRadius: 10,
                                                                            boxShadow: "0 2px 10px rgba(0,0,0,0.12)",
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    row[cell.id]
                                                                )}
                                                        </TableCell>
                                                    ))}

                                                    {rowActions && (
                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            {React.cloneElement(rowActions, { guid: row.guid })}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={filteredRows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                            />
                        </Paper>

                        <FormControlLabel
                            control={<Switch checked={dense} onChange={() => setDense(!dense)} />}
                            label="Dense padding"
                        />
                    </Box>

                    {/* ✅ Delete confirmation dialog (only if deleteEndpoint provided) */}
                    {canDelete && (
                        <Dialog open={confirmOpen} onClose={closeDeleteConfirm} maxWidth="sm" fullWidth>
                            <DialogTitle sx={{ fontWeight: 900 }}>
                                Delete {selected.length} item{selected.length === 1 ? "" : "s"}?
                            </DialogTitle>

                            <DialogContent dividers>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    This action cannot be undone.
                                </Typography>

                                {selected.length > 0 && (
                                    <Typography variant="body2" sx={{ fontWeight: 800, mt: 1 }}>
                                        Selected IDs
                                    </Typography>
                                )}

                                <Box
                                    sx={{
                                        mt: 1,
                                        maxHeight: 180,
                                        overflow: "auto",
                                        fontFamily: "monospace",
                                        fontSize: 12,
                                        bgcolor: "action.hover",
                                        borderRadius: 2,
                                        p: 1.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    {selected.map((id) => (
                                        <Box
                                            key={id}
                                            sx={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {id}
                                        </Box>
                                    ))}
                                </Box>

                                {deleteError && (
                                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                        {deleteError}
                                    </Typography>
                                )}
                            </DialogContent>

                            <DialogActions sx={{ p: 2 }}>
                                <Button onClick={closeDeleteConfirm} disabled={deleting} sx={{ textTransform: "none" }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={confirmDelete}
                                    disabled={deleting || selected.length === 0}
                                    sx={{ textTransform: "none", fontWeight: 800 }}
                                >
                                    {deleting ? "Deleting..." : "Delete"}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}
                </>
            )}
        </>
    );
}

GenericDataTable.propTypes = {
    pageUrl: PropTypes.string.isRequired,
    fetchUrl: PropTypes.string.isRequired,
    ovverideColumns: PropTypes.array,
    rowActions: PropTypes.element, // ✅ bo cloneElement
    refreshTrigger: PropTypes.any,
    tableName: PropTypes.string,
    emptyState: PropTypes.node,
    readyState: PropTypes.node,
    deleteEndpoint: PropTypes.string, // ✅ opcjonalny
};
